import { doc, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { auth, db } from '../../../../firebase';
import { authPostConfirmed } from '../../../utils/cloudFunctionsAuth';
import type { FeedEmailData } from '../components/EmailFeedContent';

/**
 * Mark an email as read in Firestore AND, for Microsoft Graph-sourced
 * emails, propagate the read state through to Graph via the
 * setGraphEmailReadState Cloud Function.
 *
 * Optimistic-with-rollback: Firestore is written first so the row flips
 * instantly, then Graph is asked. If Graph refuses (or the request fails)
 * the Firestore fields are put back to their previous values - the UI must
 * never keep claiming "read" for a message Outlook still shows as unread.
 *
 * Never throws: it resolves true only when the state is fully applied
 * (Firestore + Graph). On failure it resolves false and shows ONE error toast
 * (deduped by toastId, so a thread of N messages failing together does not
 * stack N toasts). Callers that aggregate their own summary (mark-all-read)
 * use applyEmailReadState directly and toast once themselves.
 *
 * Extracted from EmailFeedContent.handleToggleReadState so the terminal
 * workspace (thread pane expand / reply-sent handler) can mark emails
 * as read using the exact same path - no drift between the two.
 */

const SET_GRAPH_READ_URL =
  'https://us-central1-web-construct-app.cloudfunctions.net/setGraphEmailReadState';

// One shared id: while a read-failure toast is on screen, further identical
// failures (a whole thread, a burst of `e` keypresses) are swallowed.
const READ_FAILED_TOAST_ID = 'mail-read-failed';

export interface ReadFields { dismissed: boolean; isRead: boolean }
/** `forbidden` = the server said this user may not change Outlook mail at all
 *  (owner-only endpoints answer 403): permanent for the user, not a glitch. */
export type ReadWriteResult = { ok: true } | { ok: false; reason: string; forbidden?: boolean };

// Every Graph write endpoint (read state, flag, junk, delete) is owner-only and
// answers 403 to the second app user. That is not a transient failure: retrying
// or leaving the optimistic write in place would be wrong (emails/{id}.isRead is
// ONE shared doc - a local-only "read" would clear Ben's unread badge while
// Outlook still shows the message unread, and the sync fast-path would never
// correct it). So the write is rolled back, the reason says why, and the 403 is
// remembered so a thread of N messages / every later open does not flip N rows
// read and back and fire N doomed requests.
export const OWNER_ONLY_REASON = 'only the mailbox owner can do this';

/** A 403 from an owner-gated endpoint (HttpError carries .status). */
export function isForbiddenError(err: unknown): boolean {
  return (err as any)?.status === 403;
}

// Remembered per signed-in user, and for a bounded time so a stale answer can
// never lock the owner out for good (a 403 is deterministic per uid, but a
// redeploy could change who the owner is).
const FORBIDDEN_TTL_MS = 60 * 60 * 1000;
let forbiddenKey: string | null = null;
let forbiddenAt = 0;

function currentUserKey(): string {
  try { return auth?.currentUser?.uid || 'anon'; } catch { return 'anon'; }
}

/** True while this user is known to be barred from Outlook writes. */
export function isGraphWriteForbiddenNow(): boolean {
  if (!forbiddenKey) return false;
  if (Date.now() - forbiddenAt > FORBIDDEN_TTL_MS || forbiddenKey !== currentUserKey()) {
    forbiddenKey = null; // expired, or a different user signed in
    return false;
  }
  return true;
}

export function noteGraphWriteForbidden(): void {
  forbiddenKey = currentUserKey();
  forbiddenAt = Date.now();
}

/** Forget a remembered 403 (tests; also safe to call on sign-out). */
export function clearGraphWriteForbidden(): void {
  forbiddenKey = null;
  forbiddenAt = 0;
}

export function isEmailRead(email: FeedEmailData): boolean {
  if (!email) return false;
  if (email.source === 'graph') {
    const v = (email as any).isRead;
    if (typeof v === 'boolean') return v === true;
    return (email as any).dismissed === true;
  }
  return !!(email as any).dismissed;
}

/** The Firestore read-state fields as they are NOW - the rollback target. */
export function readFieldsOf(email: FeedEmailData): ReadFields {
  const read = isEmailRead(email);
  const e = email as any;
  return {
    dismissed: typeof e?.dismissed === 'boolean' ? e.dismissed : read,
    isRead: typeof e?.isRead === 'boolean' ? e.isRead : read,
  };
}

function reasonOf(err: unknown): string {
  const m = (err as any)?.message;
  return typeof m === 'string' && m.trim() ? m.trim() : 'unknown error';
}

/**
 * Write a read/unread state for one email doc with Graph propagation and
 * rollback. Pure of UI: returns the outcome, never toasts, never throws - so
 * bulk callers can aggregate failures into a single summary.
 *
 * `previous` is what to restore if Graph refuses; for a normal toggle that is
 * readFieldsOf(email), for an "undo" it is the state the undo started from.
 */
export async function applyEmailReadState(
  key: string,
  isGraph: boolean,
  isRead: boolean,
  previous: ReadFields,
): Promise<ReadWriteResult> {
  // Known owner-only: do not even flip the row - it would only flicker read and
  // then snap back unread, per message, and send a request that cannot succeed.
  if (isGraph && isGraphWriteForbiddenNow()) {
    return { ok: false, reason: OWNER_ONLY_REASON, forbidden: true };
  }
  const ref = doc(db, 'emails', key);
  try {
    // Flip both fields so every badge/counter in the app (which reads either)
    // reflects the new state.
    await updateDoc(ref, { dismissed: isRead, isRead });
  } catch (err) {
    console.error('[markEmailRead] Firestore write failed:', key, err);
    return { ok: false, reason: reasonOf(err) };
  }
  if (!isGraph) return { ok: true };

  // Graph: propagate so the user's real Outlook inbox matches. Owner-guarded
  // cloud function; authPostConfirmed throws on any non-ok answer.
  try {
    await authPostConfirmed(SET_GRAPH_READ_URL, { emailId: key, isRead });
    return { ok: true };
  } catch (err) {
    const forbidden = isForbiddenError(err);
    if (forbidden) noteGraphWriteForbidden();
    console.warn('[markEmailRead] Graph refused read-state change, rolling back:', key, err);
    try {
      await updateDoc(ref, { dismissed: previous.dismissed, isRead: previous.isRead });
    } catch (rollbackErr) {
      // Nothing more we can do here - the next delta sync re-asserts Graph's truth.
      console.error('[markEmailRead] rollback failed:', key, rollbackErr);
    }
    return forbidden
      ? { ok: false, reason: OWNER_ONLY_REASON, forbidden: true }
      : { ok: false, reason: reasonOf(err) };
  }
}

/**
 * Options for the read-state writers.
 *  - silent: never toast (the caller reports its own summary).
 *  - auto:   NOT a deliberate click - the app is marking on the person's behalf
 *            (opening a row / thread, expanding a message in a job workspace).
 *            An owner-only refusal is then swallowed (logged) instead of
 *            toasting on every open; genuine failures still toast.
 */
export interface ReadWriteOptions { silent?: boolean; auto?: boolean }

/** Set one email read/unread. true = fully applied incl. Graph. Toasts once on failure. */
export async function setEmailReadState(
  email: FeedEmailData,
  isRead: boolean,
  opts: ReadWriteOptions = {},
): Promise<boolean> {
  const key = String((email as any).emailId || email.id || '');
  if (!key) return false;
  const res = await applyEmailReadState(key, email.source === 'graph', isRead, readFieldsOf(email));
  if (res.ok) {
    console.info('[markEmailRead] set', isRead ? 'read:' : 'unread:', key, email.subject || '(no subject)');
    return true;
  }
  const quietForbidden = !!opts.auto && res.forbidden === true;
  if (!opts.silent && !quietForbidden) {
    toast.error(`couldn't mark as ${isRead ? 'read' : 'unread'}: ${res.reason}`, { toastId: READ_FAILED_TOAST_ID });
  }
  return false;
}

/** Mark read. Resolves true when already read or fully applied; false on failure. */
export async function markEmailRead(
  email: FeedEmailData,
  opts: ReadWriteOptions = {},
): Promise<boolean> {
  // Guard against double-writes - nothing to do when already read.
  if (isEmailRead(email)) return true;
  return setEmailReadState(email, true, opts);
}
